/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useEffect, useState } from "react";
import moment from "moment";
import Seasons from "../components/Seasons";
import {
  getSeasonList, addSeason, editSeasonDetails, deleteSeason,
} from "../../../api/SeasonServices";
import validateForm from "../../../common/utils/validateForm";
import Constants from "../../../common/utils/Constants";
import UserData from "../../../common/security/UserData";
import { hasAccessRights } from "../../../common/security/PermissionUtils";
import Error from "../../404/containers/ErrorContainer";

const SeasonsContainer = () => {
  const [seasonData, setSeasonData] = useState([]);
  const [hasData, setHasData] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState();
  const [addModal, setaddModalOpen] = useState(false);
  const [editModal, setEditModalOpen] = useState(false);
  const [deteleModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [addSeasonInProgress, setAddSeasonInProgress] = useState(false);
  const [editSeasonInProgress, setEditSeasonInProgress] = useState(false);
  const [deleteSeasonInProgress, setDeleteSeasonInProgress] = useState(false);
  const [message, setMessage] = useState({
    type: null,
    message: "",
  });
  const [season, setSeason] = useState({
    id: "",
    seasonId: "",
    seasonName: "",
    seasonGroup: "",
    startDate: new Date(),
    endDate: new Date(),
  });
  const methods = validateForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    register, errors, handleSubmit, clearErrors,
  } = methods;

  const [size, setSize] = useState(20);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const displaySize = [20, 50, 100];
  const { USER_PREFERENCES } = Constants;
  const { getUserPreference, setUserPreference } = UserData;

  const getSeasonsData = (searchValue, newPage, limit) => {
    setLoading(true);
    getSeasonList(searchValue, newPage, limit).then((response) => {
      setLoading(false);
      if (response && response.success && response.data) {
        const { data } = response;
        setSeasonData(data.seasons || []);
        const parsedLimit = parseInt(limit, 10);
        setPage(data.totalCount < parsedLimit ? 0 : newPage);
        setSize(data.totalCount < parsedLimit ? 20 : parsedLimit);
        setHasData(true);
        setTotalCount(data.totalCount);
        setSearchTerm(searchValue);
        if (data.seasons.length === 0) { setHasData(false); }
      } else {
        setHasData(false);
      }
    });
  };

  useEffect(() => {
    const limit = getUserPreference(USER_PREFERENCES.SEASONS_TABLE_LIMIT) || 20;
    getSeasonsData(searchTerm, page, limit);
  }, []);

  const getPageData = (newPage) => {
    getSeasonsData(searchTerm, newPage, size);
  };

  const clearModalData = () => {
    setSeason({
      seasonId: "",
      seasonName: "",
      seasonGroup: "",
      startDate: new Date(),
      endDate: new Date(),
    });
  };

  const toggleAddModal = () => {
    clearModalData();
    setMessage({ type: null, message: "" });
    setaddModalOpen(!addModal);
  };

  const handleCloseAddModal = () => {
    clearErrors("seasonId");
    clearErrors("seasonName");
    clearErrors("seasonGroup");
    clearErrors("countryId");
    clearModalData();
    setaddModalOpen(!addModal);
  };

  /**
   * This method is used to change Date state
   * @param {String} name
   * @param {String} value
   */
  const handleDateChange = (name, value) => {
    const newSeasons = { ...season, [name]: value.toDate() };
    if (name === "startDate" && value.isAfter(season.endDate)) {
      newSeasons.endDate = value.toDate();
    }
    if (name === "endDate" && value.isBefore(season.startDate)) {
      newSeasons.startDate = value.toDate();
    }
    setSeason(newSeasons);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setSeason({
      ...season,
      [name]: value,
    });
  };

  /**
 * This method is used to submit the form for Add Season
 * @param {Event} event
 */
  const submitSeasonForm = async () => {
    const requestData = {
      seasonId: season.seasonId && season.seasonId.trim(),
      seasonName: season.seasonName && season.seasonName.trim(),
      seasonGroup: season.seasonGroup && season.seasonGroup.trim(),
      countryId: season.countryId && season.countryId.trim(),
      startDate: moment(season.startDate).format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
      endDate: moment(season.endDate).format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
    };
    setAddSeasonInProgress(true);
    addSeason(requestData).then((response) => {
      if (response && response.success && response.data) {
        setAddSeasonInProgress(false);
        setaddModalOpen(false);
        setMessage({ type: "success", message: response.messages[0] });
        getSeasonsData(searchTerm, page, size);
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else if (response && response.success === false) {
        setAddSeasonInProgress(false);
        setMessage({ type: "warning", message: response.messages[0] });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else {
        setAddSeasonInProgress(false);
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setaddModalOpen(false);
          getSeasonsData(searchTerm, page, size);
        }, 3000);
      }
    });
  };

  /**
* This method is used to Open the Edit Season modal
*/
  const toggleEditModal = (data) => {
    if (data) {
      setSeason({ ...season, ...data });
      setEditModalOpen(!editModal);
    }
  };

  /**
* This method is used to Open the Edit Season modal
*/
  const handleCloseEditModal = () => {
    clearErrors("seasonId");
    clearErrors("seasonName");
    clearErrors("seasonGroup");
    clearErrors("countryId");
    clearModalData();
    setEditModalOpen(!editModal);
  };
  /**
 * This method is used to submit the Edit form
 */
  const submitEditSeasonForm = () => {
    const updatedSeasonData = {};
    updatedSeasonData.seasonId = season.seasonId && season.seasonId.trim();
    updatedSeasonData.seasonName = season.seasonName && season.seasonName.trim();
    updatedSeasonData.seasonGroup = season.seasonGroup && season.seasonGroup.trim();
    updatedSeasonData.countryId = season.countryId && season.countryId.trim();
    updatedSeasonData.startDate = moment(season.startDate).format("YYYY-MM-DD[T]HH:mm:ss[Z]");
    updatedSeasonData.endDate = moment(season.endDate).format("YYYY-MM-DD[T]HH:mm:ss[Z]");
    setEditSeasonInProgress(true);
    editSeasonDetails(season.id, updatedSeasonData).then((response) => {
      if (response && response.success && response.data) {
        setEditSeasonInProgress(false);
        setEditModalOpen(false);
        setMessage({ type: "success", message: response.messages[0] });
        getSeasonsData(searchTerm, page, size);
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else if (response && response.success === false) {
        setEditSeasonInProgress(false);
        setMessage({ type: "warning", message: response.messages[0] });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else {
        setEditSeasonInProgress(false);
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setEditModalOpen(false);
          getSeasonsData(searchTerm, page, size);
        }, 3000);
      }
    });
  };

  /**
* This method is used to toggle confirmation modal for delete season
*/

  const handleDeleteConfirmationModal = (data) => {
    if (data) {
      setSeason({ ...season, ...data });
    }
    setDeleteModal(!deteleModal);
  };

  /**
* This method is used to cancel confirm form
*/
  const cancelConfirm = () => {
    setDeleteModal(!deteleModal);
  };

  /**
*  This function is to delete a Season
*/

  const handleDeleteSeason = (id) => {
    let newPage = seasonData
      && Array.isArray(seasonData) && seasonData.length > 1 ? page : page - 1;
    if (page === 0) {
      newPage = 0;
    }
    deleteSeason(id).then((response) => {
      if (response && response.success) {
        setDeleteSeasonInProgress(true);
        setDeleteModal(!deteleModal);
        setMessage({ type: "warning", message: response.messages[0] });
        getSeasonsData(searchTerm, newPage, size);
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setDeleteSeasonInProgress(false);
        }, 3000);
      } else {
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setDeleteModal(!deteleModal);
          getSeasonsData(searchTerm, newPage, size);
        }, 3000);
      }
    });
  };

  /**
 * This method is used to update text in search box
 * @param {Event} event
 */
  const handleSearchTerm = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
  };

  /**
 * This method is used to get Season details by Search term
 */
  const handleSearch = () => {
    getSeasonsData(searchTerm.trim(), 0, size);
  };

  const handleKeyPress = (event) => {
    if (event.charCode === 13) {
      handleSearch();
    }
  };

  /**
*This method is used to toogle dropdown
*
*/
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  /**
  *This method is used to handle size
  *
  * @param newSize
  */
  const handleSelectedSize = (newSize) => {
    setUserPreference(USER_PREFERENCES.SEASONS_TABLE_LIMIT, newSize);
    getSeasonsData(searchTerm, 0, newSize);
  };

  return (
    <>
      {hasAccessRights(["ViewSeasonsLink"])
        ? (
          <Seasons
            seasonData={seasonData}
            hasData={hasData}
            isLoading={isLoading}
            page={page}
            getPageData={getPageData}
            size={size}
            totalCount={totalCount}
            addModal={addModal}
            toggleAddModal={toggleAddModal}
            handleCloseAddModal={handleCloseAddModal}
            season={season}
            handleChange={handleChange}
            submitSeasonForm={submitSeasonForm}
            handleSubmit={handleSubmit}
            register={register}
            errors={errors}
            editModal={editModal}
            toggleEditModal={toggleEditModal}
            submitEditSeasonForm={submitEditSeasonForm}
            handleCloseEditModal={handleCloseEditModal}
            deteleModal={deteleModal}
            handleDeleteConfirmationModal={handleDeleteConfirmationModal}
            cancelConfirm={cancelConfirm}
            handleDeleteSeason={handleDeleteSeason}
            handleDateChange={handleDateChange}
            searchTerm={searchTerm}
            handleSearchTerm={handleSearchTerm}
            handleSearch={handleSearch}
            handleKeyPress={handleKeyPress}
            message={message}
            addSeasonInProgress={addSeasonInProgress}
            editSeasonInProgress={editSeasonInProgress}
            deleteSeasonInProgress={deleteSeasonInProgress}
            setDeleteSeasonInProgress={setDeleteSeasonInProgress}
            displaySize={displaySize}
            toggleDropdown={toggleDropdown}
            dropdownOpen={dropdownOpen}
            handleSelectedSize={handleSelectedSize}
          />
        )
        : <Error />}
    </>
  );
};
export default SeasonsContainer;
